import type { OnboardingV3GoalType } from "@/src/hooks/onboarding/onboardingV3Types";
import { buildDebtLineSeries } from "./buildDebtLineSeries";
import { normalizeTargetProgressSeries } from "./normalizeTargetProgressSeries";
import type { ResultChartPayload } from "./types";

const toNumberArray = (value: unknown): number[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  const nums = value.filter((item): item is number => typeof item === "number");
  return nums.length > 0 ? nums : null;
};

const chartRecord = (
  values: Record<string, unknown>,
): Record<string, unknown> => {
  const chart = values.chart;
  if (!chart || typeof chart !== "object" || Array.isArray(chart)) {
    return {};
  }
  return chart as Record<string, unknown>;
};

const formatTargetLabel = (targetAmount: unknown): string => {
  if (typeof targetAmount !== "number") {
    return "$1M";
  }
  if (targetAmount >= 1_000_000) {
    return "$1M";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(targetAmount);
};

export const mapResultChartPayload = (
  goalType: OnboardingV3GoalType,
  values: Record<string, unknown>,
): ResultChartPayload | undefined => {
  const chart = chartRecord(values);

  switch (goalType) {
    case "million":
    case "retirement": {
      const onYourOwn = toNumberArray(chart.onYourOwn);
      const withEd = toNumberArray(chart.withEd);
      const onYourOwnYear = values.onYourOwnYear;
      const withEdYear = values.withEdYear;
      const deltaYears = values.deltaYears;
      if (
        !onYourOwn ||
        !withEd ||
        typeof onYourOwnYear !== "number" ||
        typeof withEdYear !== "number" ||
        typeof deltaYears !== "number"
      ) {
        return undefined;
      }
      return {
        kind: "line-comparison",
        onYourOwn: normalizeTargetProgressSeries(onYourOwn, "on-your-own"),
        withEd: normalizeTargetProgressSeries(withEd, "with-ed"),
        onYourOwnYear,
        withEdYear,
        deltaYears,
        targetLabel:
          goalType === "million"
            ? formatTargetLabel(values.targetAmount)
            : "FI target",
      };
    }
    case "emergency": {
      const current = chart.current;
      const safeTarget = chart.safeTarget;
      const withEd = chart.withEd;
      const deltaMonths = values.deltaMonths;
      if (
        typeof current !== "number" ||
        typeof safeTarget !== "number" ||
        typeof withEd !== "number" ||
        typeof deltaMonths !== "number"
      ) {
        return undefined;
      }
      return {
        kind: "runway-bars",
        current,
        safeTarget,
        withEd,
        deltaMonths,
      };
    }
    case "idlecash": {
      const currentYield = chart.currentYield;
      const withEdYield = chart.withEdYield;
      const annualExtraYield = values.annualExtraYield;
      const longTermGain = values.longTermGain;
      const longTermYears = values.longTermYears;
      const idleCashAmount = values.idleCashAmount;
      if (
        typeof currentYield !== "number" ||
        typeof withEdYield !== "number" ||
        typeof annualExtraYield !== "number" ||
        typeof longTermGain !== "number" ||
        typeof longTermYears !== "number" ||
        typeof idleCashAmount !== "number"
      ) {
        return undefined;
      }
      const startYear = new Date().getFullYear();
      const endYear = startYear + longTermYears;
      const midYear = startYear + Math.round(longTermYears * 0.53);
      return {
        kind: "idle-cash-growth",
        currentYield,
        withEdYield,
        annualExtraYield,
        longTermGain,
        longTermYears,
        idleCashAmount,
        endYear,
        midYear,
      };
    }
    case "home": {
      const onYourOwnHomePrice = values.onYourOwnHomePrice;
      const withEdHomePrice = values.withEdHomePrice;
      if (
        typeof onYourOwnHomePrice !== "number" ||
        typeof withEdHomePrice !== "number"
      ) {
        return undefined;
      }
      return {
        kind: "home-affordability",
        onYourOwnHomePrice,
        withEdHomePrice,
      };
    }
    case "debt": {
      const onYourOwnYears = chart.onYourOwnYears;
      const withEdYears = chart.withEdYears;
      const onYourOwnYear = values.onYourOwnYear;
      const withEdYear = values.withEdYear;
      if (
        typeof onYourOwnYears !== "number" ||
        typeof withEdYears !== "number" ||
        typeof onYourOwnYear !== "number" ||
        typeof withEdYear !== "number"
      ) {
        return undefined;
      }
      const { onYourOwn, withEd } = buildDebtLineSeries(
        onYourOwnYears,
        withEdYears,
      );
      return {
        kind: "line-comparison",
        onYourOwn,
        withEd,
        onYourOwnYear,
        withEdYear,
        deltaYears: Math.max(0, onYourOwnYear - withEdYear),
        targetLabel: "Debt-free",
      };
    }
    default:
      return undefined;
  }
};
